/**
 * La cámara de la vista 3D del depósito.
 *
 * Es una proyección axonométrica con dos ángulos libres: el **giro** alrededor
 * del eje vertical del depósito y la **altura** del punto de vista. Con altura
 * 90° queda una vista de planta pura —la altura deja de verse— y con 0°, un
 * alzado. Todo en metros: el SVG usa metros como unidad, así que no hay
 * conversiones intermedias que puedan quedar desfasadas.
 *
 * Vive aparte del componente porque es la parte que conviene poder probar sin
 * montar nada.
 */

export type Punto = { sx: number; sy: number };
export type Punto3 = { x: number; y: number };
export type Rect = { x: number; y: number; ancho: number; alto: number };
/** Las cuatro esquinas de una huella, en metros y en orden constante. */
export type Base = [Punto3, Punto3, Punto3, Punto3];

export const ZOOM = { min: 0.5, max: 40 };
/** Altura 0 es el alzado: se mira el depósito derecho, de frente. */
export const ALTURA = { min: 0, max: 88 };

/**
 * Grados por píxel al girar con la ruedita apretada. El giro da la vuelta
 * entera en algo más de mil píxeles y la altura recorre su rango en unos
 * trescientos: si se le pone la misma sensibilidad a los dos, el vertical
 * llega al tope enseguida y se siente trabado.
 */
export const SENSIBILIDAD = { giro: 0.32, altura: 0.22 };

/** Isométrica clásica: desde la esquina y bastante desde arriba. */
export const CAMARA_INICIAL = { giro: 45, altura: 30 };

export const grados = (g: number) => (g * Math.PI) / 180;
export const acotar = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export function hacerCamara(giroGrados: number, alturaGrados: number) {
  const g = grados(giroGrados);
  const a = grados(alturaGrados);
  const cosG = Math.cos(g);
  const senG = Math.sin(g);
  const senA = Math.sin(a);
  const cosA = Math.cos(a);

  /** Profundidad: cuanto más grande, más cerca del ojo. */
  const hacia = (x: number, y: number) => x * senG + y * cosG;

  return {
    /** Desde dónde se mira, proyectado sobre el piso. */
    ojo: { x: senG, y: cosG },
    hacia,
    proyectar: (x: number, y: number, z: number): Punto => ({
      sx: x * cosG - y * senG,
      sy: hacia(x, y) * senA - z * cosA,
    }),
    /**
     * El revés de la proyección: qué punto del piso se está viendo en cierto
     * lugar de la pantalla. Sirve para girar alrededor de un punto fijo del
     * depósito en vez de alrededor de un punto fijo de la pantalla, que es lo
     * que hace que el modelo parezca escaparse mientras uno gira.
     *
     * Devuelve null mirando casi derecho: ahí el piso se ve de canto y un
     * punto de la pantalla ya no corresponde a un punto del piso.
     */
    dePiso: (sx: number, sy: number): Punto3 | null => {
      if (Math.abs(senA) < 0.05) return null;
      const v = sy / senA;
      return { x: sx * cosG + v * senG, y: -sx * senG + v * cosG };
    },
  };
}

export type Camara = ReturnType<typeof hacerCamara>;

export const poli = (puntos: Punto[]) => puntos.map((p) => `${p.sx.toFixed(3)},${p.sy.toFixed(3)}`).join(' ');

/** Las cuatro esquinas de un rectángulo, siempre en el mismo sentido. */
export const esquinas = (r: Rect): Base => [
  { x: r.x, y: r.y },
  { x: r.x + r.ancho, y: r.y },
  { x: r.x + r.ancho, y: r.y + r.alto },
  { x: r.x, y: r.y + r.alto },
];

/** Un rectángulo apoyado en el piso, a la altura que se le indique. */
export const enElPiso = (camara: Camara, r: Rect, z = 0) =>
  poli(esquinas(r).map((p) => camara.proyectar(p.x, p.y, z)));

/**
 * Las cuatro esquinas de la huella de un tramo de muro (o del hueco de una
 * abertura): el segmento `a`-`b` centrado, con el espesor real a los dos
 * lados. Sale en el mismo sentido que `esquinas()`, así que `carasVisibles`
 * saca de acá las mismas normales «hacia afuera» que de un rectángulo derecho
 * —de eso depende que un muro no se dibuje del revés—.
 *
 * No hace falta rotar nada: la cámara proyecta cualquier par de puntos del
 * piso, así que un muro al sesgo sale bien con las mismas cuentas que uno
 * derecho. Vive acá, con la cámara, y no en la vista, porque es geometría y
 * es lo que conviene poder probar sin montar el 3D.
 */
export function baseDelTramo(a: Punto3, b: Punto3, espesorM: number): Base {
  const largo = Math.hypot(b.x - a.x, b.y - a.y) || 1; // evita ÷0 en un tramo degenerado
  const ux = (b.x - a.x) / largo;
  const uy = (b.y - a.y) / largo;
  // Perpendicular al segmento, de largo mitad-espesor.
  const px = (-uy * espesorM) / 2;
  const py = (ux * espesorM) / 2;
  return [
    { x: a.x - px, y: a.y - py },
    { x: b.x - px, y: b.y - py },
    { x: b.x + px, y: b.y + py },
    { x: a.x + px, y: a.y + py },
  ];
}

/**
 * Las caras laterales de un prisma que miran a la cámara. Con el giro libre ya
 * no son siempre las mismas dos: se queda con aquellas cuya normal hacia
 * afuera apunta al ojo. De frente se ven dos; mirando de punta, una sola.
 *
 * `adentro` es un punto del lado que se quiere mirar —el medio de la nave,
 * para un muro perimetral— y sirve para abrir el galpón: una cara cuya normal
 * apunta para el lado contrario a ese punto es la **cara de afuera** del muro,
 * la que uno vería parado en la calle, y se descarta. Sin eso, el muro que
 * queda entre la cámara y el interior tapa todo lo que hay adentro; y
 * dibujarlo antes que el resto -para que no tape- deja los racks pintados
 * encima de la pared, como si estuvieran afuera del edificio. Descartando la
 * cara de afuera, el muro cercano desaparece, el galpón se abre, y los muros
 * lejanos -de los que se ve la cara de adentro- quedan detrás de todo lo que
 * hay en el piso, que es donde corresponde: mirando desde afuera, el rayo de
 * la vista atraviesa el interior antes de llegar a esa pared.
 */
export function carasVisibles(
  base: Base,
  camara: Camara,
  z0: number,
  z1: number,
  adentro?: Punto3,
): Punto[][] {
  const caras: Punto[][] = [];
  for (let i = 0; i < 4; i++) {
    const a = base[i]!;
    const b = base[(i + 1) % 4]!;
    // Normal hacia afuera de esta cara: el borde girado un cuarto de vuelta.
    const nx = b.y - a.y;
    const ny = -(b.x - a.x);
    // La tolerancia descarta las caras de canto: mirando de frente a un eje,
    // el coseno da 6e-17 en vez de 0 y se colaba un polígono sin ancho.
    if (nx * camara.ojo.x + ny * camara.ojo.y <= Math.hypot(nx, ny) * 1e-9) continue;
    if (adentro && nx * (adentro.x - (a.x + b.x) / 2) + ny * (adentro.y - (a.y + b.y) / 2) <= 0) continue;
    caras.push([
      camara.proyectar(a.x, a.y, z1),
      camara.proyectar(b.x, b.y, z1),
      camara.proyectar(b.x, b.y, z0),
      camara.proyectar(a.x, a.y, z0),
    ]);
  }
  return caras;
}

/**
 * Estira un encuadre para que tenga la misma forma que el recuadro donde se
 * va a dibujar. Sin esto el SVG centra el dibujo y deja bandas vacías a los
 * costados o arriba y abajo, que es espacio de pantalla tirado —y hace que
 * acercarse a algo puntual no lo llene nunca.
 */
export function segunLaPantalla(caja: Rect, relacion: number): Rect {
  const ancho = Math.max(caja.ancho, caja.alto * relacion);
  const alto = ancho / relacion;
  return {
    x: caja.x + caja.ancho / 2 - ancho / 2,
    y: caja.y + caja.alto / 2 - alto / 2,
    ancho,
    alto,
  };
}

/** Caja que envuelve a un puñado de puntos ya proyectados. */
export function envolver(puntos: Punto[], margen = 0): Rect | null {
  if (puntos.length === 0) return null;
  const x1 = Math.min(...puntos.map((p) => p.sx)) - margen;
  const y1 = Math.min(...puntos.map((p) => p.sy)) - margen;
  const x2 = Math.max(...puntos.map((p) => p.sx)) + margen;
  const y2 = Math.max(...puntos.map((p) => p.sy)) + margen;
  return { x: x1, y: y1, ancho: x2 - x1, alto: y2 - y1 };
}

/**
 * Encuadre que entra una caja cualquiera del predio, con sus ocho vértices
 * proyectados. Toma la posición y no sólo la medida, porque con dos naves ya
 * no hay una sola apoyada en el origen.
 */
export function encuadrarCaja(camara: Camara, caja: Rect, altoM: number, margen = 1.5): Rect {
  const puntos: Punto[] = [];
  for (const x of [caja.x, caja.x + caja.ancho])
    for (const y of [caja.y, caja.y + caja.alto]) for (const z of [0, altoM]) puntos.push(camara.proyectar(x, y, z));

  return envolver(puntos, margen)!;
}

/** El encuadre de una nave apoyada en el origen. */
export const encuadrarNave = (camara: Camara, anchoM: number, largoM: number, altoM: number, margen = 1.5): Rect =>
  encuadrarCaja(camara, { x: 0, y: 0, ancho: anchoM, alto: largoM }, altoM, margen);

/**
 * Los ángulos al arrastrar con la ruedita apretada. La idea es que el depósito
 * siga a la mano, como si uno lo agarrara y lo hiciera girar: llevando el
 * mouse a la derecha, el frente del depósito se va a la derecha, y llevándolo
 * hacia abajo el modelo se inclina y deja ver el techo.
 */
export function orbitar(
  base: { giro: number; altura: number },
  dxPixeles: number,
  dyPixeles: number,
): { giro: number; altura: number } {
  const vuelta = base.giro - dxPixeles * SENSIBILIDAD.giro;
  return {
    giro: ((vuelta % 360) + 360) % 360,
    altura: acotar(base.altura + dyPixeles * SENSIBILIDAD.altura, ALTURA.min, ALTURA.max),
  };
}

/**
 * Acerca dejando quieto el punto que está bajo el dedo o el cursor: sin esto,
 * acercarse termina llevándote a otra parte del depósito.
 */
export function acercar(
  vista: { cx: number; cy: number; zoom: number },
  factor: number,
  ancla?: { x: number; y: number },
): { cx: number; cy: number; zoom: number } {
  const nuevo = acotar(vista.zoom * factor, ZOOM.min, ZOOM.max);
  const real = nuevo / vista.zoom;
  if (real === 1) return vista;
  if (!ancla) return { ...vista, zoom: nuevo };
  return {
    cx: ancla.x - (ancla.x - vista.cx) / real,
    cy: ancla.y - (ancla.y - vista.cy) / real,
    zoom: nuevo,
  };
}
